import React from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineEnvelope } from "react-icons/hi2";
import { FaSms } from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa6";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import { ModalShell } from "../../../atoms/ModalShell";

export type MessageChannel = "email" | "sms" | "whatsapp";

interface SendMessageChannelModalProps {
  show: boolean;
  onClose: () => void;
  hasEmailAccess: boolean;
  hasSmsAccess: boolean;
  hasWhatsappAccess: boolean;
  hasEmailCapability: boolean;
  hasSmsCapability: boolean;
  hasWhatsappCapability: boolean;
  hasValidPhone: boolean;
  onChoose: (channel: MessageChannel) => void;
}

const SendMessageChannelModal: React.FC<SendMessageChannelModalProps> = ({
  show,
  onClose,
  hasEmailAccess,
  hasSmsAccess,
  hasWhatsappAccess,
  hasEmailCapability,
  hasSmsCapability,
  hasWhatsappCapability,
  hasValidPhone,
  onChoose,
}) => {
  const { t } = useTranslation();

  const smsDisabled = hasSmsAccess && !hasValidPhone;
  const whatsappDisabled = hasWhatsappAccess && !hasValidPhone;

  const visibleCount = [
    hasEmailCapability,
    hasSmsCapability,
    hasWhatsappCapability,
  ].filter(Boolean).length;

  const gridColsClass =
    visibleCount >= 3
      ? "grid-cols-1 sm:grid-cols-3"
      : visibleCount === 2
      ? "grid-cols-1 sm:grid-cols-2"
      : "grid-cols-1";

  const modalSize = visibleCount >= 3 ? "xl" : visibleCount === 2 ? "md" : "sm";

  const Option: React.FC<{
    icon: React.ReactNode;
    label: string;
    sublabel?: string;
    locked?: boolean;
    disabled?: boolean;
    onClick: () => void;
  }> = ({ icon, label, sublabel, locked, disabled, onClick }) => (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`flex h-full w-full flex-row items-center justify-start gap-3 rounded-lg border p-4 text-left transition-colors sm:flex-col sm:justify-center sm:gap-2 sm:p-5 sm:text-center ${
        disabled
          ? "border-gray-200 bg-gray-50 cursor-not-allowed opacity-70"
          : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/50"
      }`}
    >
      <span className="inline-flex shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-blue-50 text-blue-600">
        {icon}
      </span>
      <span className="flex flex-col items-start gap-1 sm:items-center">
        <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">
          {label}
        </span>
        {locked && <UnlockBadge className="shrink-0" />}
        {sublabel && <span className="text-xs text-gray-500">{sublabel}</span>}
      </span>
    </button>
  );

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size={modalSize}
      title={t("dashboard.entities.viewEntityUser.channelChoice.title")}
      subtitle={t("dashboard.entities.viewEntityUser.channelChoice.subtitle")}
    >
      <div className={`grid gap-3 ${gridColsClass}`}>
        {hasEmailCapability && (
          <Option
            icon={<HiOutlineEnvelope size={22} />}
            label={
              t(
                "dashboard.entities.viewEntityUser.channelChoice.email"
              ) as string
            }
            locked={!hasEmailAccess}
            onClick={() => onChoose("email")}
          />
        )}
        {hasSmsCapability && (
          <Option
            icon={<FaSms size={22} />}
            label={
              t("dashboard.entities.viewEntityUser.channelChoice.sms") as string
            }
            sublabel={
              smsDisabled
                ? (t(
                    "dashboard.entities.viewEntityUser.channelChoice.invalidPhone"
                  ) as string)
                : undefined
            }
            locked={!hasSmsAccess}
            disabled={smsDisabled}
            onClick={() => onChoose("sms")}
          />
        )}
        {hasWhatsappCapability && (
          <Option
            icon={<FaWhatsapp size={22} />}
            label={
              t(
                "dashboard.entities.viewEntityUser.channelChoice.whatsapp"
              ) as string
            }
            sublabel={
              whatsappDisabled
                ? (t(
                    "dashboard.entities.viewEntityUser.channelChoice.invalidPhone"
                  ) as string)
                : undefined
            }
            locked={!hasWhatsappAccess}
            disabled={whatsappDisabled}
            onClick={() => onChoose("whatsapp")}
          />
        )}
      </div>
    </ModalShell>
  );
};

export default SendMessageChannelModal;
